<script lang="ts">
  import { onDestroy } from "svelte";
  import { showMenu } from "../../stores/menuStore";
  import { link, location } from "svelte-spa-router";
  import { user } from "../../stores/userStore";
  import Svg from "../UI/Svg.svelte";
  import { notifications, unreadNotifications, unreadAiActions } from "../../stores/notificationStore";
  import { unreadMessages } from "../../stores/chatSessionsStore";
  import { Capacitor } from "@capacitor/core";
  import { isClient } from "../../lib/roles";
  import { useSocket } from "../../lib/hooks/useSocket";
  import { loadedWorkouts } from "stores";
  import { updateNotificationStatuses } from "../../lib/fetchNotifications";

  let active: string;
  let isTrainer: boolean;
  let dietLink: string;
  let trainingLink: string;
  let chatLink: string;

  const position = Capacitor.isNativePlatform() ? "fixed" : "sticky";

  const unsubscribeLocation = location.subscribe((res) => {
    active = $location;
    if (
      res === "/chat" ||
      res.includes("/auth") ||
      res.includes("/login") ||
      res.includes("/autologin-handoff") ||
      res.includes("/chat-with")
    ) {
      $showMenu = false;
    } else {
      $showMenu = true;
    }
  });

  const unsubscribeUser = user.subscribe((res) => {
    isTrainer = !isClient(res);
    dietLink = isTrainer ? "/diet/meal-plans" : "/diet/meal-plan";
    trainingLink = isTrainer ? "/training/programs" : "/training/program";
    chatLink = isTrainer ? "/chat-list" : "/chat";
  });

  const socket = useSocket({ componentName: 'Menu' });
  const {isConnected} = socket;
  
  // Subscribe to socket events when connected
  $: if ($user && $isConnected) {
    socket.emit("subscribe", { event: "unreadCount", id: $user.id });
    socket.emit("subscribe", { event: "notification", id: $user.id });
    
    socket.on("unreadCount", (updatedUnreadCount) => {
      $unreadMessages = updatedUnreadCount.unreadCount;
    });
    
    socket.on("notification", (receivedNotification) => {
      if (!$notifications.find((n) => n.id === receivedNotification.id)) {
        $unreadNotifications += 1;
        $notifications = [receivedNotification, ...$notifications];
        $loadedWorkouts = {};
        updateNotificationStatuses();
      }
    });
  }

  onDestroy(() => {
    if ($isConnected && $user) {
      socket.emit("unsubscribe", { event: "notification", id: $user.id });
      socket.emit("unsubscribe", { event: "unreadCount", id: $user.id });
    }
    unsubscribeLocation();
    unsubscribeUser();
  });
</script>

{#if $showMenu && $user}
  <div
    class={`menu ${position} flex-row center-space-around bg-white dark:bg-zinc-800 border-t border-slate-300 dark:border-zinc-700`}
  >
    {#if !isTrainer}
      <a href="/progress/measurements" use:link>
        <Svg
          name={active.includes("/progress") ? "progress-fill" : "progress"}
          size={24}
          customColor={active.includes("/progress") ? `bg-primary-500 dark:bg-primary-500` : ""}
        />
      </a>
    {/if}
    <a href={dietLink} use:link>
      <Svg
        name={active.includes("/diet") ? "utensils-fill" : "utensils"}
        size={24}
        customColor={active.includes("/diet") ? `bg-primary-500 dark:bg-primary-500` : ""}
      />
    </a>
    <a href={trainingLink} use:link>
      <Svg
        name={active.includes("/training") ? "dumbbell-fill" : "dumbbell"}
        size={24}
        customColor={active.includes("/training") ? `bg-primary-500 dark:bg-primary-500` : ""}
      />
    </a>
    {#if isTrainer}
      <a href="/clients/list" use:link>
        <Svg
          name={active.includes("/clients") ? "users-fill" : "users"}
          size={24}
          customColor={active.includes("/clients") ? `bg-primary-500 dark:bg-primary-500` : ""}
        />
      </a>
    {/if}
    {#if $user?.isChatDisabled}
      <div class="relative cursor-not-allowed">
        <Svg name="message" size={24} customColor="bg-gray-500"/>
      </div>
    {:else}
      <a href={chatLink} use:link>
        <div class="relative">
          <Svg
            name={active.includes("/chat") ? "message-fill" : "message"}
            size={24}
            customColor={active.includes("/chat") ? `bg-primary-500 dark:bg-primary-500` : ""}
          />
          {#if $unreadMessages > 0}
            <div class="notification-bubble flex-row center-center">
              {$unreadMessages}
            </div>
          {/if}
        </div>
      </a>
    {/if}
    <a href="/notifications" use:link>
      <div style="position: relative;">
        <Svg
          name={active.includes("/notifications") ? "bell-fill" : "bell"}
          size={23}
          customColor={active.includes("/notifications") ? `bg-primary-500 dark:bg-primary-500` : ""}
        />
        {#if $unreadNotifications > 0}
          <div class="notification-bubble flex-row center-center">
            {$unreadNotifications}
          </div>
        {/if}
        {#if $unreadAiActions > 0}
          <!-- Purple dot — reads as "AI activity" rather than
               "another notification". The exact count lives on the
               "AI" tab chip in the top menu; this just signals presence.
               Sits diagonally opposite the red notifications bubble. -->
          <div class="ai-dot" />
        {/if}
      </div>
    </a>
    <a href="/settings" use:link>
      <Svg
        name={active.includes("/settings") ? "gear-fill" : "gear"}
        size={24}
        customColor={active.includes("/settings") ? `bg-primary-500 dark:bg-primary-500` : ""}
      />
    </a>
  </div>
{/if}

<style>
  a {
    -webkit-tap-highlight-color: transparent;
    text-decoration: none;
  }
  .menu {
    bottom: 0;
    left: 0;
    max-width: 720px;
    height: 48px;
    width: 100%;
    padding-top: calc(
      10px +
      calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom)) / 2) +
      var(--app-menu-icon-top-extra)
    );
    padding-bottom: calc(6px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom)) + var(--app-bottom-extra));
  }
  .notification-bubble {
    position: absolute;
    top: -8px;
    right: -6px;
    height: 16px;
    min-width: 16px;
    padding: 0 2px;
    background-color: red;
    border-radius: 64px;
    color: #fff;
    font-size: 8px;
    font-weight: 700;
    line-height: 1;
  }
  /* AI purple dot on the bell icon. Tucked into the bottom-right so
     it sits diagonally opposite the red notifications bubble (which
     anchors top-right). Small, subtle — just signals presence; the
     exact count lives on the "AI" tab chip inside the notifications
     screen. Pointer-events off so it doesn't intercept taps meant
     for the bell link. */
  .ai-dot {
    position: absolute;
    bottom: 10px;
    right: -2px;
    height: 4px;
    width: 4px;
    background-color: #9333ea; /* tailwind purple-600 */
    border-radius: 9999px;
    box-shadow: 0 0 0 1.5px var(--bell-dot-ring, #fff);
    pointer-events: none;
  }
  :global(.dark) .ai-dot {
    --bell-dot-ring: #27272a; /* zinc-800 — matches dark menu bg */
  }
</style>
